/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import { useState, useEffect } from 'react';

const ONBOARDING_STORAGE_KEY = 'guituitive-onboarding-completed';
const ONBOARDING_VERSION = '1.1'; // Increment to show onboarding again

interface UseOnboardingReturn {
  shouldShowOnboarding: boolean;
  shouldShowTutorialPrompt: boolean;
  showOnboarding: () => void;
  completeOnboarding: () => void;
  skipOnboarding: () => void;
  resetOnboarding: () => void;
  dismissTutorialPrompt: () => void;
}

export const useOnboarding = (): UseOnboardingReturn => {
  const [shouldShowOnboarding, setShouldShowOnboarding] = useState(false);
  const [shouldShowTutorialPrompt, setShouldShowTutorialPrompt] = useState(false);

  useEffect(() => {
    const checkOnboardingStatus = () => {
      try {
        const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
        const onboardingData = stored ? JSON.parse(stored) : null;
        
        // Show onboarding if:
        // 1. Version has changed (new features added) for existing users
        // 2. User explicitly requested it
        const shouldShow = (onboardingData && onboardingData.version !== ONBOARDING_VERSION) ||
                          (onboardingData && onboardingData.showAgain === true);
        
        // Show tutorial prompt if:
        // 1. No onboarding data exists (first time user)
        // 2. Prompt hasn't been dismissed and onboarding not completed
        const shouldShowPrompt = !onboardingData || 
                                (!onboardingData.completed && 
                                 !onboardingData.skipped && 
                                 !onboardingData.promptDismissed);
        
        setShouldShowOnboarding(shouldShow);
        setShouldShowTutorialPrompt(shouldShowPrompt && !shouldShow); // Don't show prompt if onboarding is already showing
      } catch (error) {
        console.warn('Error checking onboarding status:', error);
        // If there's an error reading storage, show onboarding to be safe
        setShouldShowOnboarding(true);
      }
    };

    checkOnboardingStatus();
  }, []);

  const showOnboarding = () => {
    setShouldShowOnboarding(true);
    setShouldShowTutorialPrompt(false); // Hide prompt when onboarding starts
  };

  const completeOnboarding = () => {
    try {
      const onboardingData = {
        completed: true,
        version: ONBOARDING_VERSION,
        completedAt: new Date().toISOString(),
        showAgain: false
      };
      
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(onboardingData));
      setShouldShowOnboarding(false);
    } catch (error) {
      console.warn('Error saving onboarding completion status:', error);
      setShouldShowOnboarding(false);
    }
  };

  const skipOnboarding = () => {
    try {
      const onboardingData = {
        completed: false,
        skipped: true,
        version: ONBOARDING_VERSION,
        skippedAt: new Date().toISOString(),
        showAgain: false
      };
      
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(onboardingData));
      setShouldShowOnboarding(false);
    } catch (error) {
      console.warn('Error saving onboarding skip status:', error);
      setShouldShowOnboarding(false);
    }
  };

  const resetOnboarding = () => {
    try {
      localStorage.removeItem(ONBOARDING_STORAGE_KEY);
      setShouldShowOnboarding(true);
      setShouldShowTutorialPrompt(false);
    } catch (error) {
      console.warn('Error resetting onboarding status:', error);
    }
  };

  const dismissTutorialPrompt = () => {
    try {
      const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
      const onboardingData = stored ? JSON.parse(stored) : {};
      
      const updatedData = {
        ...onboardingData,
        promptDismissed: true,
        promptDismissedAt: new Date().toISOString(),
        version: ONBOARDING_VERSION
      };
      
      localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(updatedData));
      setShouldShowTutorialPrompt(false);
    } catch (error) {
      console.warn('Error dismissing tutorial prompt:', error);
      setShouldShowTutorialPrompt(false);
    }
  };

  return {
    shouldShowOnboarding,
    shouldShowTutorialPrompt,
    showOnboarding,
    completeOnboarding,
    skipOnboarding,
    resetOnboarding,
    dismissTutorialPrompt
  };
};

export default useOnboarding;